import type { OnwardQuoteSuggestion } from 'hooks/useOnwardBookings';
import useSessionStorage from 'hooks/useSessionStorage';
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';

export type BookingStep = 'journey' | 'vehicle' | 'confirmation'

export type JourneyPhase = 'account' | 'route' | 'schedule'

export interface MapLocationData {
  pickup?: any
  dropoff?: any
  stops?: any[]
}

export interface MapFlightArc {
  from: { latitude: number; longitude: number; name?: string; iata?: string }
  to: { latitude: number; longitude: number; name?: string; iata?: string }
}

export interface RouteInfo {
  estimatedJourneyDistance?: number
  estimatedJourneyTime?: number
}

export interface BookingWizardData {
  currentStep: BookingStep
  setStep: (step: BookingStep) => void

  journeyPhase: JourneyPhase
  setJourneyPhase: (phase: JourneyPhase) => void

  journeyData: Record<string, any>
  setJourneyData: (data: any) => void

  accountData: Record<string, any>
  setAccountData: (data: any) => void

  vehicleData: Record<string, any>
  setVehicleData: (data: any) => void

  preview: Record<string, any>
  setPreview: (data: any) => void

  routeInfo: RouteInfo | undefined
  setRouteInfo: (info: RouteInfo | undefined) => void

  mapLocations: MapLocationData
  setMapLocations: (data: MapLocationData) => void
  updateMapPickup: (pickup: any) => void
  updateMapDropoff: (dropoff: any) => void
  updateMapStops: (stops: any[]) => void

  goNext: () => void
  goBack: () => void
  clearAll: () => void

  onwardSuggestions: OnwardQuoteSuggestion[]
  setOnwardSuggestions: (suggestions: OnwardQuoteSuggestion[]) => void

  mapFlightArc: MapFlightArc | undefined
  setMapFlightArc: (arc: MapFlightArc | undefined) => void

  // Undefined until something triggers the cinematic flyover. The map
  // component treats `undefined` as "do not run" so an arc that draws on
  // its own (e.g. when the flight number resolves) doesn't accidentally
  // play the animation.
  flightArcFlyoverKey: number | undefined
  triggerMapFlightArcFlyover: () => void

  isLoadingQuotes: boolean
  setIsLoadingQuotes: (loading: boolean) => void

  // Set when ConfirmationStep successfully creates a booking. Overlay
  // components key off this to know "the booking is done - show post-booking
  // affordances like the onward suggestions."
  confirmedBookingId: string | undefined
  setConfirmedBookingId: (id: string | undefined) => void

  isOnwardJourney: boolean
  setIsOnwardJourney: (value: boolean) => void

  isQuickQuote: boolean
  isCopyBooking: boolean
}

const STEP_ORDER: BookingStep[] = ['journey', 'vehicle', 'confirmation']
const JOURNEY_PHASE_ORDER: JourneyPhase[] = ['account', 'route', 'schedule']

const WIZARD_STATE_PROP = '__bookingWizardStep__'
const JOURNEY_PHASE_PROP = '__journeyPhase__'

function readInnerHistoryState(raw: unknown): Record<string, unknown> | null {
  if (typeof raw !== 'object' || raw === null) return null
  const wrapper = raw as Record<string, unknown>
  return typeof wrapper.state === 'object' && wrapper.state !== null
    ? (wrapper.state as Record<string, unknown>)
    : wrapper
}

function readWizardStepFromHistoryState(raw: unknown): BookingStep | null {
  const inner = readInnerHistoryState(raw)
  if (!inner) return null
  const candidate = inner[WIZARD_STATE_PROP]
  if (typeof candidate !== 'string') return null
  if (!(STEP_ORDER as string[]).includes(candidate)) return null
  return candidate as BookingStep
}

function readJourneyPhaseFromHistoryState(raw: unknown): JourneyPhase | null {
  const inner = readInnerHistoryState(raw)
  if (!inner) return null
  const candidate = inner[JOURNEY_PHASE_PROP]
  if (typeof candidate !== 'string') return null
  if (!(JOURNEY_PHASE_ORDER as string[]).includes(candidate)) return null
  return candidate as JourneyPhase
}

function makeRouterShapedState(
  step: BookingStep,
  phase: JourneyPhase,
): {
  key: string
  state: { [k: string]: BookingStep | JourneyPhase }
} {
  return {
    key: Math.random().toString(36).substr(2, 6),
    state: {
      [WIZARD_STATE_PROP]: step,
      [JOURNEY_PHASE_PROP]: phase,
    },
  }
}

function hasData(d: Record<string, any> | undefined | null): boolean {
  return !!d && Object.keys(d).length > 0
}

function getMaxReachableStep(
  journeyData: Record<string, any> | undefined | null,
  vehicleData: Record<string, any> | undefined | null,
): BookingStep {
  if (!hasData(journeyData)) return 'journey'
  if (!hasData(vehicleData)) return 'vehicle'
  return 'confirmation'
}

function clampStep(requested: BookingStep, max: BookingStep): BookingStep {
  const reqIdx = STEP_ORDER.indexOf(requested)
  const maxIdx = STEP_ORDER.indexOf(max)
  return reqIdx > maxIdx ? max : requested
}

function furtherPhase(a: JourneyPhase, b: JourneyPhase): JourneyPhase {
  return JOURNEY_PHASE_ORDER.indexOf(a) >= JOURNEY_PHASE_ORDER.indexOf(b) ? a : b
}

const BookingWizardContext = createContext<BookingWizardData | undefined>(
  undefined,
)

export function useBookingWizardContext(): BookingWizardData {
  const context = useContext(BookingWizardContext)
  if (!context) {
    throw new Error(
      'useBookingWizardContext must be used within BookingWizardProvider',
    )
  }
  return context
}

interface UseBookingWizardOptions {
  initialStep?: BookingStep
  isQuickQuote?: boolean
  isCopyBooking?: boolean
}

export function useBookingWizard(
  options: UseBookingWizardOptions = {},
): BookingWizardData {
  const {
    initialStep = 'journey',
    isQuickQuote = false,
    isCopyBooking = false,
  } = options

  const [currentStep, setCurrentStepRaw] = useSessionStorage(
    'booking_current_step',
    initialStep,
  ) as [BookingStep, (step: BookingStep) => void]

  const [journeyPhase, setJourneyPhaseState] = useSessionStorage(
    'booking_journey_phase',
    'account',
  ) as [JourneyPhase, (phase: JourneyPhase) => void]

  const [journeyData, setJourneyData] = useSessionStorage(
    'booking_journey_data',
    {},
  )
  const [accountData, setAccountData] = useSessionStorage(
    'booking_account_data',
    {},
  )
  const [vehicleData, setVehicleData] = useSessionStorage(
    'booking_vehicle_data',
    {},
  )
  const [preview, setPreview] = useSessionStorage('booking_preview', {})

  const [routeInfo, setRouteInfo] = useState<RouteInfo | undefined>(undefined)

  const [onwardSuggestions, setOnwardSuggestions] = useState<
    OnwardQuoteSuggestion[]
  >([])

  const [mapLocations, setMapLocations] = useState<MapLocationData>({})

  const [mapFlightArc, setMapFlightArc] = useState<MapFlightArc | undefined>(
    undefined,
  )

  const [flightArcFlyoverKey, setFlightArcFlyoverKey] = useState<
    number | undefined
  >(undefined)
  const triggerMapFlightArcFlyover = useCallback(() => {
    setFlightArcFlyoverKey((k) => (k ?? 0) + 1)
  }, [])

  const [isLoadingQuotes, setIsLoadingQuotes] = useState<boolean>(false)

  const [confirmedBookingId, setConfirmedBookingId] = useState<
    string | undefined
  >(undefined)

  const [isOnwardJourney, setIsOnwardJourney] = useSessionStorage(
    'booking_is_onward_journey',
    false,
  )

  const currentStepRef = useRef<BookingStep>(currentStep)
  const journeyPhaseRef = useRef<JourneyPhase>(journeyPhase)
  const journeyDataRef = useRef<Record<string, any>>(journeyData)
  const vehicleDataRef = useRef<Record<string, any>>(vehicleData)
  currentStepRef.current = currentStep
  journeyPhaseRef.current = journeyPhase
  journeyDataRef.current = journeyData
  vehicleDataRef.current = vehicleData

  const setJourneyDataSynced = useCallback(
    (next: any) => {
      journeyDataRef.current =
        typeof next === 'function' ? next(journeyDataRef.current) : next
      setJourneyData(journeyDataRef.current)
    },
    [setJourneyData],
  )
  const setVehicleDataSynced = useCallback(
    (next: any) => {
      vehicleDataRef.current =
        typeof next === 'function' ? next(vehicleDataRef.current) : next
      setVehicleData(vehicleDataRef.current)
    },
    [setVehicleData],
  )

  const setCurrentStepRawRef = useRef(setCurrentStepRaw)
  setCurrentStepRawRef.current = setCurrentStepRaw

  const setStepRaw = useCallback((step: BookingStep) => {
    currentStepRef.current = step
    setCurrentStepRawRef.current(step)
  }, [])

  const setJourneyPhaseRaw = useCallback((phase: JourneyPhase) => {
    journeyPhaseRef.current = phase
    setJourneyPhaseState(phase)
  }, [])

  const updateMapPickup = useCallback((pickup: any) => {
    setMapLocations((prev: MapLocationData) => ({ ...prev, pickup }))
  }, [])

  const updateMapDropoff = useCallback((dropoff: any) => {
    setMapLocations((prev: MapLocationData) => ({ ...prev, dropoff }))
  }, [])

  const updateMapStops = useCallback((stops: any[]) => {
    setMapLocations((prev: MapLocationData) => ({ ...prev, stops }))
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const onPopState = (event: PopStateEvent) => {
      const poppedStep = readWizardStepFromHistoryState(event.state)
      const poppedPhase = readJourneyPhaseFromHistoryState(event.state)
      if (!poppedStep && !poppedPhase) return
      if (poppedStep) {
        const max = getMaxReachableStep(
          journeyDataRef.current,
          vehicleDataRef.current,
        )
        const target = clampStep(poppedStep, max)
        if (target !== currentStepRef.current) {
          setStepRaw(target)
        }
      }
      if (poppedPhase && poppedPhase !== journeyPhaseRef.current) {
        setJourneyPhaseRaw(poppedPhase)
      }
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [setStepRaw, setJourneyPhaseRaw])

  const seededRef = useRef(false)
  useEffect(() => {
    if (seededRef.current) return
    if (typeof window === 'undefined') return

    const params = new URLSearchParams(window.location.search)
    if (params.get('new') === 'true') {
      return
    }
    seededRef.current = true

    const pickupFilled = Boolean(
      journeyDataRef.current?.outbound?.pickup?.latitude,
    )
    const restoredPhase = journeyPhaseRef.current
    const heuristicPhase: JourneyPhase = pickupFilled ? 'route' : 'account'
    const initialPhase: JourneyPhase = furtherPhase(restoredPhase, heuristicPhase)

    const existingStep = readWizardStepFromHistoryState(window.history.state)
    const existingPhase = readJourneyPhaseFromHistoryState(window.history.state)
    if (existingStep) {
      const max = getMaxReachableStep(
        journeyDataRef.current,
        vehicleDataRef.current,
      )
      const target = clampStep(existingStep, max)
      if (target !== currentStepRef.current) {
        setStepRaw(target)
      }
      const phase = furtherPhase(existingPhase ?? initialPhase, restoredPhase)
      if (phase !== journeyPhaseRef.current) {
        setJourneyPhaseRaw(phase)
      }
      return
    }

    const max = getMaxReachableStep(
      journeyDataRef.current,
      vehicleDataRef.current,
    )
    const target = clampStep(currentStepRef.current, max)
    const targetIdx = STEP_ORDER.indexOf(target)
    const url = window.location.pathname + window.location.search

    window.history.replaceState(
      makeRouterShapedState(STEP_ORDER[0], 'account'),
      '',
      url,
    )
    const journeyPhaseCount = Math.max(
      JOURNEY_PHASE_ORDER.indexOf(initialPhase),
      targetIdx > 0 ? JOURNEY_PHASE_ORDER.indexOf('route') : 0,
    )
    for (let p = 1; p <= journeyPhaseCount; p++) {
      window.history.pushState(
        makeRouterShapedState(STEP_ORDER[0], JOURNEY_PHASE_ORDER[p]),
        '',
        url,
      )
    }
    for (let i = 1; i <= targetIdx; i++) {
      window.history.pushState(
        makeRouterShapedState(STEP_ORDER[i], 'route'),
        '',
        url,
      )
    }

    if (target !== currentStepRef.current) {
      setStepRaw(target)
    }
    if (initialPhase !== journeyPhaseRef.current) {
      setJourneyPhaseRaw(initialPhase)
    }
  })

  const setStep = useCallback(
    (requested: BookingStep) => {
      const max = getMaxReachableStep(
        journeyDataRef.current,
        vehicleDataRef.current,
      )
      const target = clampStep(requested, max)
      if (target === currentStepRef.current) return

      setStepRaw(target)
      if (typeof window !== 'undefined') {
        const url = window.location.pathname + window.location.search
        window.history.pushState(
          makeRouterShapedState(target, journeyPhaseRef.current),
          '',
          url,
        )
      }
    },
    [setStepRaw],
  )

  const setJourneyPhase = useCallback(
    (nextPhase: JourneyPhase) => {
      const prevPhase = journeyPhaseRef.current
      if (nextPhase === prevPhase) return
      setJourneyPhaseRaw(nextPhase)
      if (typeof window === 'undefined') return
      const url = window.location.pathname + window.location.search
      const routerState = makeRouterShapedState(
        currentStepRef.current,
        nextPhase,
      )
      const goingForward =
        JOURNEY_PHASE_ORDER.indexOf(nextPhase) >
        JOURNEY_PHASE_ORDER.indexOf(prevPhase)
      if (goingForward) {
        window.history.pushState(routerState, '', url)
      } else {
        window.history.replaceState(routerState, '', url)
      }
    },
    [setJourneyPhaseRaw],
  )

  const goNext = useCallback(() => {
    const idx = STEP_ORDER.indexOf(currentStepRef.current)
    if (idx < STEP_ORDER.length - 1) setStep(STEP_ORDER[idx + 1])
  }, [setStep])

  const goBack = useCallback(() => {
    const idx = STEP_ORDER.indexOf(currentStepRef.current)
    if (idx > 0) setStep(STEP_ORDER[idx - 1])
  }, [setStep])

  const clearAll = useCallback(() => {
    setStepRaw('journey')
    setJourneyPhaseRaw('account')
    setJourneyData({})
    journeyDataRef.current = {}
    setAccountData({})
    setVehicleData({})
    vehicleDataRef.current = {}
    setPreview({})
    setRouteInfo(undefined)
    setMapLocations({})
    setOnwardSuggestions([])
    setMapFlightArc(undefined)
    setIsLoadingQuotes(false)
    setConfirmedBookingId(undefined)
    setIsOnwardJourney(false)

    if (typeof window !== 'undefined') {
      const url = window.location.pathname + window.location.search
      window.history.replaceState(
        makeRouterShapedState('journey', 'account'),
        '',
        url,
      )
    }
  }, [
    setStepRaw,
    setJourneyPhaseRaw,
    setJourneyData,
    setAccountData,
    setVehicleData,
    setPreview,
    setIsOnwardJourney,
  ])

  return {
    currentStep,
    setStep,
    journeyPhase,
    setJourneyPhase,
    journeyData,
    setJourneyData: setJourneyDataSynced,
    accountData,
    setAccountData,
    vehicleData,
    setVehicleData: setVehicleDataSynced,
    preview,
    setPreview,
    routeInfo,
    setRouteInfo,
    mapLocations,
    setMapLocations,
    updateMapPickup,
    updateMapDropoff,
    updateMapStops,
    goNext,
    goBack,
    clearAll,
    onwardSuggestions,
    setOnwardSuggestions,
    mapFlightArc,
    setMapFlightArc,
    flightArcFlyoverKey,
    triggerMapFlightArcFlyover,
    isLoadingQuotes,
    setIsLoadingQuotes,
    confirmedBookingId,
    setConfirmedBookingId,
    isOnwardJourney,
    setIsOnwardJourney,
    isQuickQuote,
    isCopyBooking,
  }
}

interface BookingWizardProviderProps {
  children: ReactNode
  initialStep?: BookingStep
  isQuickQuote?: boolean
  isCopyBooking?: boolean
}

export function BookingWizardProvider({
  children,
  initialStep,
  isQuickQuote = false,
  isCopyBooking = false,
}: BookingWizardProviderProps): JSX.Element {
  const wizard = useBookingWizard({
    initialStep,
    isQuickQuote,
    isCopyBooking,
  })

  return (
    <BookingWizardContext.Provider value={wizard}>
      {children}
    </BookingWizardContext.Provider>
  )
}

export { BookingWizardContext };
